import React, { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { MainCharacter } from '@/customTypes/storygame';
import { AiProfileInfo } from '@/customTypes/chapter';
import StoryGameCharacterListError from '@/components/commons/popup/StoryGameCharacterListError';
import useTranslation from 'next-translate/useTranslation';

interface CharacterImageProps {
  character: AiProfileInfo;
}

const CharacterImage = ({ character }: CharacterImageProps) => {
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (character.profileFile) {
      const url = URL.createObjectURL(character.profileFile);
      setProfileImageUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setProfileImageUrl(null);
    }
  }, [character.profileFile]);

  const imageSrc = profileImageUrl || character.defaultProfileImageFile?.link;

  if (!imageSrc) {
    return (
      <>
        <div className="character-image-border"></div>
        <div className="character-image" />
      </>
    );
  }

  return (
    <>
      <div className="character-image-border"></div>
      <img
        src={imageSrc}
        alt={character.firstName}
        className="character-image"
      />
    </>
  );
};

const StoryGameCharacterList = ({
  characterList,
  onClickStoryGameCharacter,
  selectedCharacter,
}: {
  characterList?: AiProfileInfo[];
  onClickStoryGameCharacter: (character: AiProfileInfo) => void;
  selectedCharacter?: MainCharacter;
}) => {
  const { t } = useTranslation();
  const handleCharacterClick = (character: AiProfileInfo) => {
    onClickStoryGameCharacter(character);
  };

  return (
    <div css={style}>
      <h2 className="character-list-title">
        {t('storygame_ai_onboarding_screen_title_character_list')}
      </h2>
      <div className="character-list">
        <ul className="character-items">
          {characterList?.map((character) => (
            <li
              key={character.characterId}
              className={`character-item ${
                selectedCharacter?.id === character.characterId
                  ? 'selected'
                  : ''
              }`}
              onClick={() => handleCharacterClick(character)}
            >
              <CharacterImage character={character} />
              <div className="character-info">
                <div className="character-name">{character.displayName}</div>
                <div className="character-description">
                  {character.isError ? (
                    <StoryGameCharacterListError />
                  ) : character.lastName || character.firstName ? (
                    `${character.lastName}${character.firstName}`
                  ) : (
                    t(
                      'storygame_ai_onboarding_screen_description_character_name',
                    )
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const style = css`
  height: 100%;
  display: flex;
  flex-direction: column;
  min-width: 270px;
  .character-list {
    width: 100%;
    margin: 0 auto;

    overflow-y: scroll;
  }

  .character-list-title {
    font-size: 20px;
    color: #fff;
    margin-top: 20px;
    margin-bottom: 16px;
    font-weight: 500;
  }

  .character-items {
    display: flex;
    flex-direction: column;
  }

  .character-item {
    display: flex;
    align-items: center;
    padding: 10px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    border-radius: 8px;
  }

  .character-item:last-child {
    border-bottom: none;
  }

  .character-item.selected {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .character-image-border {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin-right: 15px;
    object-fit: cover;
    border-radius: 128px;
    border: 1px solid var(--blackA10, rgba(0, 0, 0, 0.1));
    position: absolute;
    z-index: 1;
  }

  .character-image {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    margin-right: 15px;
    object-fit: cover;
    background: white;
  }

  .character-info {
    flex-grow: 1;
  }

  .character-name {
    font-size: 16px;
    color: #fff;
    margin-bottom: 5px;
    font-weight: 500;
  }

  .character-description {
    height: 20px;
    font-size: 14px;
    color: #ffffffbf;
  }
`;

export default StoryGameCharacterList;
